<script setup>
  import { defineProps, defineEmits, nextTick, onBeforeUnmount, onDeactivated, ref, watch } from 'vue'
  import { formatTranscriptTime, transcriptPlaybackLabel } from './transcriptTime'

  const props = defineProps({
    playback: { type: Object, required: true },
    seekRequest: { type: Object, default: null },
    loading: Boolean,
  })
  const emit = defineEmits(['position', 'refresh', 'playing', 'prepare'])
  const audio = ref(null)
  const speed = ref(1)
  const failed = ref(false)
  const playError = ref('')
  const position = ref(0)
  let pending = null
  let resume = false
  let stopped = false

  const stop = () => {
    stopped = true
    pending = null
    resume = false
    audio.value?.pause()
    audio.value?.removeAttribute('src')
    audio.value?.load()
  }
  const play = async () => {
    if (stopped) return
    try {
      await audio.value?.play()
    } catch (error) {
      if (error.name !== 'AbortError') playError.value = '请点击播放继续收听'
    }
  }
  const applySeek = () => {
    if (stopped || !audio.value || audio.value.readyState < 1) return
    if (pending != null) {
      const duration = audio.value.duration
      audio.value.currentTime = Number.isFinite(duration) ? Math.min(pending, Math.max(0, duration - 0.01)) : pending
      pending = null
      position.value = audio.value.currentTime * 1000
      emit('position', position.value)
    }
    audio.value.playbackRate = speed.value
    if (resume) {
      resume = false
      play()
    }
  }
  const requestRefresh = () => {
    pending = audio.value?.currentTime || position.value / 1000
    resume = !audio.value?.paused
    audio.value?.pause()
    emit('refresh')
  }
  const onPlay = () => {
    playError.value = ''
    if (props.playback.expiresAtMs && props.playback.expiresAtMs <= Date.now() + 1000) {
      resume = true
      pending = audio.value.currentTime
      audio.value.pause()
      emit('refresh')
      return
    }
    emit('playing', true)
  }
  const onError = () => {
    if (!stopped) {
      failed.value = true
      emit('playing', false)
    }
  }
  const update = () => {
    position.value = (audio.value?.currentTime || 0) * 1000
    emit('position', position.value)
  }
  const skip = (seconds) => {
    if (!audio.value || audio.value.readyState < 1) return
    audio.value.currentTime = Math.max(0, Math.min(audio.value.duration || Infinity, audio.value.currentTime + seconds))
  }
  watch(
    () => props.playback,
    async (value) => {
      audio.value?.pause()
      failed.value = false
      if (value.status !== 'AVAILABLE') return
      stopped = false
      await nextTick()
      if (audio.value) {
        audio.value.src = value.playbackUrl
        audio.value.load()
      }
    },
    { immediate: true }
  )
  watch(
    () => props.seekRequest,
    (request) => {
      if (!request || props.playback.status !== 'AVAILABLE') return
      pending = request.offsetMs / 1000
      resume = true
      applySeek()
    }
  )
  watch(speed, () => {
    if (audio.value) audio.value.playbackRate = speed.value
  })
  onDeactivated(stop)
  onBeforeUnmount(stop)
</script>

<template>
  <div :aria-busy="loading" class="original-player">
    <div class="player-heading">
      <span class="source-dot" />
      现场原声
      <span class="position">{{ formatTranscriptTime(position) }}</span>
    </div>
    <template v-if="playback.status === 'AVAILABLE'">
      <audio
        ref="audio"
        aria-label="现场原声播放器"
        controls
        preload="metadata"
        @ended="emit('playing', false)"
        @error="onError"
        @loadedmetadata="applySeek"
        @pause="emit('playing', false)"
        @play="onPlay"
        @timeupdate="update"
      />
      <div
        v-if="playback.retainedAudioMs != null && playback.mediaDurationMs - playback.retainedAudioMs > 1000"
        class="player-error"
      >
        部分录音缺口已补静音
      </div>
      <div class="player-actions">
        <el-button size="small" @click="skip(-10)">后退 10 秒</el-button>
        <el-button size="small" @click="skip(10)">前进 10 秒</el-button>
        <el-select v-model="speed" aria-label="播放倍速" class="speed-select" size="small">
          <el-option v-for="rate in [0.5, 0.75, 1, 1.25, 1.5, 2]" :key="rate" :label="`${rate} 倍速`" :value="rate" />
        </el-select>
        <span v-if="failed || playError" class="player-error">{{ failed ? '原声加载失败' : playError }}</span>
        <el-button v-if="failed" :loading="loading" size="small" @click="requestRefresh">重新加载</el-button>
      </div>
    </template>
    <div v-else class="player-empty">
      <span>
        {{ loading ? '正在获取原声状态…' : transcriptPlaybackLabel(playback) }}
      </span>
      <div class="empty-actions">
        <el-button v-if="playback.canPrepare" :loading="loading" size="small" type="primary" @click="emit('prepare')">
          {{ playback.status === 'FAILED' ? '重试准备' : '准备原声' }}
        </el-button>
        <el-button :loading="loading" size="small" @click="emit('refresh')">刷新</el-button>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .original-player {
    flex-shrink: 0;
    background: #fff;
    border-top: 1px solid #ebeef5;
    padding: 12px 20px;
    box-shadow: 0 -6px 18px rgb(0 0 0 / 3%);
  }
  .player-heading {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    font-size: 14px;
    font-weight: 600;
    color: #303133;
  }
  .source-dot {
    width: 6px;
    height: 6px;
    background: #409eff;
    border-radius: 50%;
  }
  .position {
    margin-left: auto;
    color: #909399;
    font-size: 12px;
    font-variant-numeric: tabular-nums;
    font-weight: 400;
  }
  audio {
    width: 100%;
    height: 40px;
  }
  .player-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
  }
  .speed-select {
    width: 100px;
  }
  .player-error {
    color: #e6a23c;
    font-size: 12px;
  }
  .empty-actions {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
  }
  .player-empty {
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: #909399;
    font-size: 14px;
  }
</style>
